import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { startGoogleLogin } from "@/pages/landing/primitives";
import AuthShell from "./AuthShell";
import GoogleButton from "./GoogleButton";

const MIN_LEN = 8;

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  const pwLongEnough = password.length >= MIN_LEN;
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = pwLongEnough && emailLooksValid;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!canSubmit) { setErr("Enter a valid email and a password with at least 8 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/register", { email: email.trim(), password, name: name.trim() });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sign-up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (resending) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: email.trim() });
      setResent(true);
      toast.success("Verification email sent again.");
    } catch { toast.error("Couldn't send the email. Try again in a moment."); }
    finally { setResending(false); }
  };

  if (sent) {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle={`We sent a verification link to ${email}. Click it to activate your account.`}
        testId="signup-sent-page"
        footer={
          <>Wrong email? <button onClick={() => setSent(false)} className="text-purple-700 font-semibold hover:underline" data-testid="signup-change-email">Start over</button></>
        }
      >
        <div className="space-y-4 text-sm text-slate-700">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="signup-sent-notice">
            The link is valid for the next 24 hours. Once you verify, you&apos;ll be signed in automatically.
          </div>
          <button
            onClick={resend}
            disabled={resending || resent}
            data-testid="signup-resend-btn"
            className="w-full border-2 border-black rounded-xl px-4 py-3 font-semibold bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            {resending ? <Loader2 className="w-5 h-5 animate-spin inline" /> : resent ? "Verification email sent" : "Resend verification email"}
          </button>
          <button
            onClick={() => navigate(`/login${location.search}`)}
            data-testid="signup-goto-login"
            className="w-full text-sm text-slate-600 hover:text-slate-900"
          >
            Back to sign-in
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start building your first business site in minutes."
      testId="signup-page"
      footer={
        <>Already have an account? <Link to={`/login${location.search}`} className="text-purple-700 font-semibold hover:underline" data-testid="signup-goto-login-link">Sign in</Link></>
      }
    >
      <GoogleButton label="Continue with Google" onClick={startGoogleLogin} testId="signup-google-btn" />
      <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px flex-1 bg-slate-200" />
        <span>OR</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Name</label>
          <input
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="signup-name"
            className="w-full border-2 border-slate-200 focus:border-black rounded-xl px-4 py-3 outline-none transition"
            placeholder="Your name"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="signup-email"
            className="w-full border-2 border-slate-200 focus:border-black rounded-xl px-4 py-3 outline-none transition"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Password</label>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="signup-password"
              className="w-full border-2 border-slate-200 focus:border-black rounded-xl px-4 py-3 pr-11 outline-none transition"
              placeholder="At least 8 characters"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              data-testid="signup-toggle-pw"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="mt-2 text-xs flex items-center gap-1.5" data-testid="signup-pw-strength">
            {pwLongEnough
              ? <><Check className="w-3.5 h-3.5 text-emerald-600" /><span className="text-emerald-700">Password looks good</span></>
              : <><X className="w-3.5 h-3.5 text-slate-400" /><span className="text-slate-500">At least {MIN_LEN} characters</span></>}
          </div>
        </div>
        {err && (
          <div data-testid="signup-error" className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={loading || !canSubmit}
          data-testid="signup-submit"
          className="w-full inline-flex items-center justify-center gap-2 iti-btn-primary px-4 py-3 font-semibold disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create account"}
        </button>
        <p className="text-[11px] text-slate-500 leading-snug">
          By creating an account you agree to the <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </form>
    </AuthShell>
  );
}
